import { grey } from '@mui/material/colors';
import Student from '../../../model/Student';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CustomTable from '../CustomTable';
import { useContext, useEffect, useRef, useState } from 'react';
import {
	getSecondBStageStudents,
	publishSecondBQualification,
	updateSecondBStageDescription,
	updateSecondBStageResult,
} from '../../../api/content-api';
import { loginPage } from '../../../store/fixed-routing';
import AuthContext from '../../../store/auth-context';
import SnackbarContext from '../../../store/snackbar-context';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../ui/Loader';
import ActionButtons from './ActionButtons';
import Result from '../../../model/Result';
import OlympicsContext from '../../../store/olympics-context';
import { DEFAULT_API_ERROR, DEFAULT_AUTH_ERROR } from '../../../utils/auth-tools';
import { cleanUpHtml, decodeBase64Utf8 } from '../../../utils/string-tools';
import { applyAutoOralFormatting } from '../../../utils/oral-description';
import { filter } from '../StagesTable';
import { debugError } from '../../../utils/debug';
import ContactLinks from '../ContactLinks';
import ProtocolVerificationDialog from './ProtocolVerificationDialog';
import Olympics from '../../../model/Olympics';
import ConfirmationDialog from '../ConfirmationDialog';
import { isSmallOlympicVariant } from '../../../config/variant-profile';

const SecondBStageTable = () => {
	const [allStudents, setAllStudents] = useState<Student[]>([]);
	const [displayedStudents, setDisplayedStudents] = useState<Student[]>([]);
	const [editedStudent, setEditedStudent] = useState<Student>();
	const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
	const [descriptionStudent, setDescriptionStudent] = useState<Student | undefined>();
	const [descriptionValue, setDescriptionValue] = useState('');
	const [originalDescriptionValue, setOriginalDescriptionValue] = useState('');
	const [isSavingDescription, setIsSavingDescription] = useState(false);
	const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
	const [confirmDescriptionCloseText, setConfirmDescriptionCloseText] = useState<string | null>(null);
	const descriptionInputRef = useRef<HTMLTextAreaElement | null>(null);
	const [importDialogOpen, setImportDialogOpen] = useState(false);
	const [importText, setImportText] = useState('');
	const [importReport, setImportReport] = useState<Array<{ status: 'success' | 'error'; message: string }>>([]);
	const [importLoading, setImportLoading] = useState(false);
	const importReportEndRef = useRef<HTMLDivElement | null>(null);
	const [verificationDialogOpen, setVerificationDialogOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(true);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isPublishingQualification, setIsPublishingQualification] = useState(false);
	const { logout, token, isSecretary, isAdmin, id } = useContext(AuthContext);
	const { olympics, districtFilterId, searchFilterValue, setOlympics, refreshTablesTrigger, setIsRefreshing } =
		useContext(OlympicsContext);
	const isRefreshingRef = useRef(false);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();
	const { palette } = useTheme();
	const editable =
		olympics && (isAdmin || !(olympics?.secondStagePoints?.partB?.stageClosed || olympics?.secondBStagePublished));
	const isSmallOlympic = isSmallOlympicVariant();
	const showOralDescription = isSmallOlympic;

	const filterStudents = (list: Student[]) => {
		let filtered = filter(list, districtFilterId);

		if (searchFilterValue) {
			filtered = filtered.filter((s) => {
				const matchesSearch = (value: string | undefined) =>
					value && value.toLowerCase().includes(searchFilterValue.toLowerCase());

				return (
					matchesSearch(s.firstName) ||
					matchesSearch(s.lastName) ||
					matchesSearch(s.email) ||
					matchesSearch(s.school?.name) ||
					matchesSearch(s.district?.name) ||
					s.teachers?.some((t: any) => matchesSearch(t.firstName) || matchesSearch(t.lastName))
				);
			});
		}

		return filtered;
	};

	useEffect(() => {
		setDisplayedStudents(filterStudents(allStudents));
	}, [districtFilterId, searchFilterValue, allStudents]);

	useEffect(() => {
		if (importReport.length > 0) {
			requestAnimationFrame(() => {
				importReportEndRef.current?.scrollIntoView({ behavior: 'smooth' });
			});
		}
	}, [importReport]);

	const getSecondBStagePoints = (s: Student) => {
		if (isSmallOlympic) {
			return s.result.secondBThreeStagePoints;
		}
		return s.result.secondBOneStagePoints + s.result.secondBTwoStagePoints + s.result.secondBThreeStagePoints;
	};

	const getSecondStageTotalPoints = (s: Student) => {
		return getSecondBStagePoints(s) + s.result.secondAOneStagePoints + s.result.secondATwoStagePoints;
	};

	const handlePublishQualification = () => {
		if (!isAdmin || olympics?.secondBStageQualificationPublished) {
			return;
		}
		const confirmed = window.confirm('Czy na pewno opublikować informację o kwalifikacji do etapu III?');
		if (!confirmed) {
			return;
		}
		setIsPublishingQualification(true);
		publishSecondBQualification(token)
			.then((response) => {
				setIsPublishingQualification(false);
				if (response.status === 200) {
					setOlympics(Olympics.fromApiResponse(response.data));
					setMsg({ msg: 'Opublikowano kwalifikację do etapu III', severity: 'success' });
				} else {
					debugError('SecondBStageTable publish qualification response error:', response);
					setMsg({ msg: response.data });
				}
			})
			.catch((exception) => {
				setIsPublishingQualification(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
			});
	};

	const downloadData = (isRefresh = false) => {
		if (isRefresh) {
			isRefreshingRef.current = true;
			setIsRefreshing(true);
		} else {
			setIsLoading(true);
		}
		getSecondBStageStudents(token)
			.then((response) => {
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
				} else {
					debugError('SecondBStageTable response error:', response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
				if (isRefresh && isRefreshingRef.current) {
					isRefreshingRef.current = false;
					setIsRefreshing(false);
				}
			});
	};

	const handleSaveFirstStageResult = () => {
		setIsUpdating(true);
		const data = {
			id: editedStudent?.id,
			secondBOneStagePoints: editedStudent?.result.secondBOneStagePoints,
			secondBTwoStagePoints: editedStudent?.result.secondBTwoStagePoints,
			secondBThreeStagePoints: editedStudent?.result.secondBThreeStagePoints,
			secondBOralDescription: editedStudent?.result.secondBOralDescription,
			secondBStageQualified: isAdmin ? editedStudent?.result.secondBStageQualified : undefined,
		};
		updateSecondBStageResult(token, data)
			.then((response) => {
				setIsUpdating(false);
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
					setEditedStudent(undefined);
					setMsg({ msg: 'Dane ucznia zostały zaktualizowane', severity: 'success' });
					const warningEncoded = response.headers?.['x-olympics-warning-b64'];
					const warningFallback = response.headers?.['x-olympics-warning'];
					if (warningEncoded || warningFallback) {
						const warning = warningEncoded ? decodeBase64Utf8(warningEncoded) : warningFallback;
						setMsg({ msg: warning, severity: 'warning' });
					}
				} else {
					debugError('SecondBStageTable response error:', response);
					setMsg({ msg: response.data });
				}
				setIsLoading(false);
			})
			.catch((exception) => {
				setIsUpdating(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
				setIsLoading(false);
			});
	};

	useEffect(() => {
		if (refreshTablesTrigger === 0) {
			downloadData(false);
		} else {
			downloadData(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [refreshTablesTrigger, token]);

	const handlePointsChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = Number(event.target.value);
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const handleTextChange = (event: any, accessor: keyof Result) => {
		if (editedStudent) {
			const result = { ...editedStudent.result };
			// @ts-ignore
			result[accessor] = event.target.value;
			setEditedStudent({
				...editedStudent,
				result: result,
			});
		}
	};

	const openDescriptionDialog = (student: Student) => {
		const currentValue = student.result.secondBOralDescription || '';
		setDescriptionStudent(student);
		setDescriptionValue(currentValue);
		setOriginalDescriptionValue(currentValue);
		setDescriptionDialogOpen(true);
	};

	const finishDescriptionDialogClose = () => {
		setConfirmDescriptionCloseText(null);
		setDescriptionDialogOpen(false);
		setDescriptionStudent(undefined);
		setDescriptionValue('');
		setOriginalDescriptionValue('');
	};

	const requestCloseDescriptionDialog = (skipConfirm = false) => {
		if (!skipConfirm && descriptionValue !== originalDescriptionValue) {
			setConfirmDescriptionCloseText('Masz niezapisane zmiany. Czy chcesz zamknąć bez zapisywania?');
			return;
		}
		finishDescriptionDialogClose();
	};

	const handleSaveDescription = () => {
		if (!descriptionStudent) {
			return;
		}
		setIsSavingDescription(true);
		updateSecondBStageDescription(token, {
			id: descriptionStudent.id,
			secondBOralDescription: descriptionValue,
		})
			.then((response) => {
				setIsSavingDescription(false);
				if (response.status === 200) {
					const students = response.data.map((d: any) => Student.fromApiResponse(d));
					setAllStudents(students);
					setDisplayedStudents(filterStudents(students));
					setMsg({ msg: 'Charakterystyka została zapisana', severity: 'success' });
					finishDescriptionDialogClose();
				} else {
					setMsg({ msg: response.data });
				}
			})
			.catch((exception) => {
				setIsSavingDescription(false);
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: DEFAULT_AUTH_ERROR });
				} else {
					const msg = exception?.response?.data?.message ? exception?.response?.data?.message : DEFAULT_API_ERROR;
					setMsg({ msg });
				}
			});
	};

	const openDescriptionPreview = (student: Student) => {
		const currentValue = student.result.secondBOralDescription || '';
		setDescriptionStudent(student);
		setDescriptionValue(currentValue);
		setOriginalDescriptionValue(currentValue);
		setPreviewDialogOpen(true);
	};

	const handleDescriptionKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'b') {
			return;
		}
		event.preventDefault();
		const target = event.target as HTMLTextAreaElement;
		const start = target.selectionStart ?? 0;
		const end = target.selectionEnd ?? 0;
		if (start === end) {
			return;
		}
		const before = descriptionValue.slice(0, start);
		const selected = descriptionValue.slice(start, end);
		const after = descriptionValue.slice(end);
		const updated = `${before}<b>${selected}</b>${after}`;
		setDescriptionValue(updated);
		requestAnimationFrame(() => {
			if (descriptionInputRef.current) {
				const cursorPosition = end + 7;
				descriptionInputRef.current.focus();
				descriptionInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
			}
		});
	};

	const normalizeName = (value: string) => value.toLowerCase().replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();

	const parseImportEntries = (text: string) => {
		const lines = text.replace(/\r\n/g, '\n').split('\n');
		const isDescriptionHeaderLine = (line: string) => {
			const trimmed = line.trim();
			if (!trimmed) {
				return false;
			}
			const normalized = trimmed.replace(/[()]/g, '').toLowerCase();
			return normalized.includes('charakterystyka wypowiedzi') && trimmed.length <= 45;
		};
		while (lines.length > 0 && isDescriptionHeaderLine(lines[0])) {
			lines.shift();
		}
		const entries: Array<{
			index: string;
			fullName: string;
			points: number;
			description: string;
			title: string;
			linesCount: number;
			charsCount: number;
		}> = [];
		const errors: string[] = [];
		let hasHeader = false;
		let current: any = null;
		let descriptionLines: string[] = [];

		const isHeaderLine = (line: string) => /lp\.?/i.test(line) && /imię i nazwisko/i.test(line) && /autor/i.test(line);
		const isPointsHeaderLine = (line: string) => /liczba/i.test(line) && /punkt/i.test(line);
		const isEntryLine = (line: string) => /^\s*\d+\.\s*\t/.test(line) || /^\s*\d+\.\s+\S/.test(line);

		const pushCurrent = () => {
			if (!current) {
				return;
			}
			const cleanedLines = descriptionLines
				.filter((line) => !isDescriptionHeaderLine(line))
				.map((line) => line.replace(/\s+$/g, ''));
			while (cleanedLines.length > 0 && cleanedLines[cleanedLines.length - 1].trim() === '') {
				cleanedLines.pop();
			}
			const description = cleanedLines.join('\n').trim();
			current.description = description;
			current.linesCount = cleanedLines.filter((line) => line.trim() !== '').length;
			current.charsCount = description.length;
			entries.push(current);
			current = null;
			descriptionLines = [];
		};

		lines.forEach((line) => {
			if (!hasHeader && isHeaderLine(line)) {
				hasHeader = true;
				return;
			}
			if (!hasHeader) {
				return;
			}
			if (isPointsHeaderLine(line)) {
				return;
			}
			if (isEntryLine(line)) {
				pushCurrent();
				const rawParts = line.includes('\t') ? line.split('\t') : line.split(/\s{2,}/);
				const parts = rawParts.map((p) => p.trim()).filter((p) => p);
				if (parts.length < 3) {
					errors.push(`Nie rozpoznano wiersza: "${line}"`);
					return;
				}
				const index = parts[0];
				const fullName = parts[1];
				const pointsRaw = parts[parts.length - 1];
				const points = Number(pointsRaw.replace(/[^\d]/g, ''));
				if (Number.isNaN(points)) {
					errors.push(`Nie rozpoznano punktów dla "${fullName}" (wiersz: "${line}")`);
					return;
				}
				const title = parts.slice(2, parts.length - 1).join(' ');
				current = { index, fullName, points, title, description: '', linesCount: 0, charsCount: 0 };
				return;
			}
			if (current && !isHeaderLine(line) && !isPointsHeaderLine(line)) {
				descriptionLines.push(line);
			}
		});
		pushCurrent();

		if (!hasHeader) {
			errors.push('Nie znaleziono nagłówka tabeli (Lp., Imię i nazwisko, Autor i tytuł).');
		}

		return { entries, errors };
	};

	const handleImport = async () => {
		if (!isAdmin) {
			return;
		}
		if (!importText.trim()) {
			setImportReport([{ status: 'error', message: 'Brak wklejonego tekstu.' }]);
			return;
		}
		if (districtFilterId === -1) {
			setImportReport([{ status: 'error', message: 'Wybierz okręg przed importem.' }]);
			return;
		}

		setImportLoading(true);
		setImportReport([]);

		const report: Array<{ status: 'success' | 'error'; message: string }> = [];
		const { entries, errors } = parseImportEntries(importText);
		errors.forEach((message) => report.push({ status: 'error', message }));
		const totalLines = entries.reduce((sum, entry) => sum + (entry.linesCount || 0), 0);
		const totalChars = entries.reduce((sum, entry) => sum + (entry.charsCount || 0), 0);
		report.unshift({
			status: 'success',
			message: `Podsumowanie: wpisów ${entries.length}, linie ${totalLines}, znaki ${totalChars}.`,
		});

		const candidates = allStudents.filter((s: Student) => s.district?.id === districtFilterId);
		for (const entry of entries) {
			const normalizedEntry = normalizeName(entry.fullName);
			const matches = candidates.filter(
				(s: Student) => normalizeName(`${s.firstName} ${s.lastName}`) === normalizedEntry,
			);
			const countsInfo = `linie ${entry.linesCount}, znaki ${entry.charsCount}${
				entry.charsCount > 10000 ? ' (UWAGA: > 10000)' : ''
			}`;
			if (matches.length === 0) {
				report.push({
					status: 'error',
					message: `Nie znaleziono ucznia: ${entry.fullName}. (${countsInfo})`,
				});
				continue;
			}
			if (matches.length > 1) {
				report.push({
					status: 'error',
					message: `Wiele osób o nazwie: ${entry.fullName}. (${countsInfo})`,
				});
				continue;
			}
			const student = matches[0];
			const expectedPoints = getSecondBStagePoints(student);
			if (expectedPoints !== entry.points) {
				report.push({
					status: 'error',
					message: `Niezgodne punkty dla ${entry.fullName}: ${entry.points} ≠ ${expectedPoints}. (${countsInfo})`,
				});
				continue;
			}
			if (!entry.description) {
				report.push({
					status: 'error',
					message: `Brak opisu dla ${entry.fullName}. (${countsInfo})`,
				});
				continue;
			}
			const existingDescription = (student.result.secondBOralDescription || '').trim();
			if (existingDescription) {
				const incomingDescription = entry.description.trim();
				const same = existingDescription === incomingDescription;
				report.push({
					status: 'success',
					message: `Pominięto ${entry.fullName} (opis już istnieje, ${
						same ? 'taka sama treść' : 'INNA TREŚĆ!!!'
					}). (${countsInfo})`,
				});
				continue;
			}
			if (entry.charsCount > 10000) {
				report.push({
					status: 'error',
					message: `Za długi opis dla ${entry.fullName} (limit 10000). (${countsInfo})`,
				});
				continue;
			}

			try {
				await updateSecondBStageDescription(token, {
					id: student.id,
					secondBOralDescription: entry.description,
				});
				report.push({
					status: 'success',
					message: `Zapisano: ${entry.fullName}. (${countsInfo})`,
				});
			} catch (exception: any) {
				const msg = exception?.response?.data?.message || exception?.message || `Błąd zapisu: ${entry.fullName}.`;
				report.push({ status: 'error', message: `${msg} (${countsInfo})` });
			}
		}

		setImportReport((prev) => [...report, ...prev]);
		setImportLoading(false);
		if (report.some((item) => item.status === 'success')) {
			downloadData(false);
		}
	};

	const handleClearImport = () => {
		setImportText('');
		setImportReport([]);
	};

	const renderPointsField = (i: Student, accessor: keyof Result) => {
		return editedStudent?.id === i.id ? (
			<TextField
				variant={'standard'}
				type={'number'}
				value={editedStudent.result[accessor]}
				disabled={!editable}
				sx={{
					width: '100%',
					height: '100%',
					justifyContent: 'end',
					'& input': {
						pl: '7%',
						minHeight: '100%',
						margin: 0,
						fontSize: '16px',
						textAlign: 'center',
					},
				}}
				onChange={(event) => handlePointsChange(event, accessor)}
			/>
		) : (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{i.result[accessor]}
			</Typography>
		);
	};

	const renderSumField = (i: Student) => {
		let value = 0;
		if (editedStudent?.id === i.id) {
			value = getSecondBStagePoints(editedStudent);
		} else {
			value = getSecondBStagePoints(i);
		}
		value = isNaN(value) ? 0 : value;
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{value}
			</Typography>
		);
	};

	const renderDescriptionField = (i: Student, accessor: keyof Result) => {
		const descriptionValue = i.result[accessor] as string;
		const descriptionLength = descriptionValue ? descriptionValue.length : 0;
		const label = descriptionLength > 0 ? `${descriptionLength} znaków` : '-brak-';
		const tooltipLabel = descriptionLength > 0 ? 'Podgląd/Edytuj' : 'Dodaj';
		return (
			<Box
				data-sort={descriptionLength}
				sx={{ display: 'flex', alignItems: 'center' }}
			>
				<Typography
					variant='caption'
					fontWeight='medium'
				>
					{label}
				</Typography>
				{editable ? (
					<>
						<Tooltip title={tooltipLabel}>
							<IconButton
								size='small'
								onClick={() => openDescriptionDialog(i)}
								sx={{ ml: 1, padding: 0 }}
							>
								<EditNoteIcon fontSize='small' />
							</IconButton>
						</Tooltip>
						{descriptionLength > 0 && (
							<Tooltip title={'Podgląd'}>
								<IconButton
									size='small'
									onClick={() => openDescriptionPreview(i)}
									sx={{ ml: 0.5, padding: 0 }}
								>
									<VisibilityIcon fontSize='small' />
								</IconButton>
							</Tooltip>
						)}
					</>
				) : (
					descriptionLength > 0 && (
						<Tooltip title={'Podgląd'}>
							<IconButton
								size='small'
								onClick={() => openDescriptionPreview(i)}
								sx={{ ml: 1, padding: 0 }}
							>
								<VisibilityIcon fontSize='small' />
							</IconButton>
						</Tooltip>
					)
				)}
			</Box>
		);
	};

	const renderTotalSumField = (i: Student) => {
		let value = 0;
		if (editedStudent?.id === i.id) {
			value = getSecondStageTotalPoints(editedStudent);
		} else {
			value = i.result.secondStageTotalPoints;
		}
		value = isNaN(value) ? 0 : value;
		return (
			<Typography
				variant='caption'
				fontWeight='medium'
			>
				{value}
			</Typography>
		);
	};

	const handleQualifiedChange = () => {
		if (editedStudent) {
			setEditedStudent({
				...editedStudent,
				result: { ...editedStudent.result, secondBStageQualified: !editedStudent.result.secondBStageQualified },
			});
		}
	};

	const renderQualifiedField = (i: Student) => {
		const value =
			editedStudent?.id === i.id && isAdmin
				? editedStudent.result.secondBStageQualified
				: i.result.secondBStageQualified;
		return (
			<Checkbox
				checked={value}
				disabled={editedStudent?.id !== i.id || !isAdmin}
				onChange={handleQualifiedChange}
			/>
		);
	};

	const data = (input: any) => {
		const showQualificationColumn = isAdmin || olympics?.secondBStageQualificationPublished;
		const contactColumns = isSecretary ? [{ header: 'Kontakt', accessor: 'contact', align: 'left' }] : [];
		let additionalColumns: any[] = [];

		if (isAdmin || isSecretary) {
			additionalColumns = [
				{ header: 'Imię', accessor: 'firstName', align: 'left' },
				{ header: 'Nazwisko', accessor: 'lastName', align: 'left' },
			];
		}

		const baseColumns: any[] = [
			{ header: 'Lp.', accessor: 'index', align: 'left', width: '70px' },
			{ header: 'Identyfikator', accessor: 'id', align: 'center', width: '150px' },
			{ header: 'Tytuł', accessor: 'personalTitle', align: 'left', width: '100px' },
			...additionalColumns,
			...contactColumns,
			{ header: 'Szkoła', accessor: 'school', align: 'left', bgColor: grey[100] },
			{ header: 'Okręg', accessor: 'district', align: 'left', width: '140px' },
			{ header: 'Nauczyciel', accessor: 'teachers', align: 'left', width: '160px' },
			{ header: 'Klasa', accessor: 'schoolClass', width: '100px', align: 'center' },
			{ header: 'Specjalizacja', accessor: 'specialization', width: '150px', align: 'center' },
		];

		const pointsColumns = isSmallOlympic
			? [{ header: 'Punkty IIB', accessor: 'points3', width: '120px', align: 'center', editable: editable }]
			: [
					{ header: 'Pytanie I', accessor: 'points1', width: '100px', align: 'center', editable: editable },
					{ header: 'Pytanie II', accessor: 'points2', width: '100px', align: 'center', editable: editable },
					{ header: 'Pytanie III', accessor: 'points3', width: '110px', align: 'center', editable: editable },
					{ header: 'Suma ustne', accessor: 'sum', width: '110px', align: 'center' },
				];

		const oralDescriptionColumns = showOralDescription
			? [
					{
						header: isAdmin ? (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
								<Typography
									sx={{
										fontSize: '16px !important',
										fontWeight: '600',
										color: palette.primary.main,
										whiteSpace: 'nowrap',
										flexGrow: 1,
									}}
								>
									<Tooltip title='Charakterystyka wypowiedzi'>
										<span>Charakterystyka</span>
									</Tooltip>
								</Typography>
								<Tooltip title='Importuj charakterystyki (Etap IIB)'>
									<IconButton
										size='small'
										onClick={(event) => {
											event.preventDefault();
											event.stopPropagation();
											setImportDialogOpen(true);
										}}
										onMouseDown={(event) => {
											event.preventDefault();
											event.stopPropagation();
										}}
									>
										<FileUploadIcon fontSize='small' />
									</IconButton>
								</Tooltip>
							</Box>
						) : (
							<Tooltip title='Charakterystyka wypowiedzi'>
								<span>Charakterystyka</span>
							</Tooltip>
						),
						accessor: 'oralDescription',
						align: 'left',
						width: '220px',
						editable: editable,
					},
				]
			: [];

		const publishQualificationHeader = isAdmin ? (
			<Tooltip
				title={
					olympics?.secondBStageQualificationPublished
						? 'Kwalifikacja do etapu III została już opublikowana'
						: 'Opublikuj informację o kwalifikacji do etapu III'
				}
			>
				<span>
					<Button
						size='small'
						variant='outlined'
						disabled={Boolean(olympics?.secondBStageQualificationPublished) || isPublishingQualification}
						onClick={handlePublishQualification}
						sx={{ minWidth: 'auto', fontSize: '12px', px: 1 }}
					>
						Publ. kwal.
					</Button>
				</span>
			</Tooltip>
		) : (
			''
		);

		return {
			columns: [
				...baseColumns,
				...pointsColumns,
				...oralDescriptionColumns,
				{ header: 'Łącznie Etap II', accessor: 'totalSum', width: '150px', align: 'center' },
				{
					header: 'Zakwalifikowany',
					accessor: 'qualified',
					width: '170px',
					align: 'center',
					editable: isAdmin,
					hidden: !showQualificationColumn,
				},
				{
					header: publishQualificationHeader,
					accessor: 'action',
					align: 'center',
					width: '150px',
					nosort: true,
					editable: editable,
				},
			],

			rows: input
				? input.map((i: Student, index: number) => {
						return {
							index: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{index + 1}
								</Typography>
							),
							id: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district.id.toString().padStart(4, '0') + '-' + i.id.toString().padStart(6, '0')}
								</Typography>
							),
							personalTitle: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.personalTitle}
								</Typography>
							),
							contact: isSecretary && <ContactLinks student={i} />,
							firstName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.firstName}
								</Typography>
							),
							lastName: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.lastName}
								</Typography>
							),
							school: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.school.name}, {i.school.cityWithCountry}
								</Typography>
							),
							district: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.district?.name || ''}
								</Typography>
							),
							teachers: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{(i.teachers || [])
										.map((t) => `${t.firstName} ${t.lastName}`.trim())
										.filter(Boolean)
										.join(', ')}
								</Typography>
							),
							schoolClass: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.schoolClassText && i.schoolClassText.trim() !== '' ? i.schoolClassText : String(i.schoolClass)}
								</Typography>
							),
							specialization: (
								<Typography
									variant='caption'
									fontWeight='medium'
								>
									{i.specialization.name}
								</Typography>
							),
							points1: renderPointsField(i, 'secondBOneStagePoints'),
							points2: renderPointsField(i, 'secondBTwoStagePoints'),
							points3: renderPointsField(i, 'secondBThreeStagePoints'),
							sum: renderSumField(i),
							oralDescription: showOralDescription ? renderDescriptionField(i, 'secondBOralDescription') : null,
							totalSum: renderTotalSumField(i),
							qualified: renderQualifiedField(i),
							action: (
								<ActionButtons
									student={i}
									disabled={!editable}
									editedStudent={editedStudent}
									inProgress={isUpdating}
									handleSave={handleSaveFirstStageResult}
									handleClose={() => setEditedStudent(undefined)}
									handleEdit={() => setEditedStudent(i)}
								/>
							),
						};
					})
				: [],
		};
	};
	const footerButtons = isAdmin ? (
		<Tooltip title='Weryfikacja protokołu (Etap IIB)'>
			<IconButton
				size='small'
				sx={{ color: 'white' }}
				onClick={() => setVerificationDialogOpen(true)}
			>
				<FileUploadIcon fontSize='small' />
			</IconButton>
		</Tooltip>
	) : null;

	return (
		<>
			{isLoading ? (
				<Loader />
			) : (
				<CustomTable
					data={data(displayedStudents)}
					footerLeft={footerButtons}
				/>
			)}
			{showOralDescription && (
				<Dialog
					open={descriptionDialogOpen}
					onClose={() => requestCloseDescriptionDialog()}
					fullWidth
					maxWidth='md'
				>
					<DialogTitle>Charakterystyka wypowiedzi (Etap 2B)</DialogTitle>
					<DialogContent>
						<Typography
							variant='body2'
							sx={{ mb: 2 }}
						>
							Możesz wkleić tekst z dokumentu. Nagłówki zostaną pogrubione automatycznie. Ręczne pogrubienie: użyj{' '}
							<code>{'<b>...'}</code> lub zaznacz tekst i naciśnij Ctrl+B.
						</Typography>
						<TextField
							fullWidth
							multiline
							minRows={10}
							value={descriptionValue}
							onChange={(event) => setDescriptionValue(event.target.value)}
							onKeyDown={handleDescriptionKeyDown}
							disabled={!editable}
							placeholder='Wklej opis...'
							inputRef={descriptionInputRef}
						/>
						<Typography
							variant='caption'
							sx={{ display: 'block', mt: 1 }}
						>
							Liczba znaków: {descriptionValue.length}
						</Typography>
					</DialogContent>
					<DialogActions>
						<Button
							variant='outlined'
							onClick={() => setPreviewDialogOpen(true)}
						>
							Podgląd z formatowaniem
						</Button>
						<Button onClick={() => requestCloseDescriptionDialog()}>Zamknij</Button>
						<Button
							variant='contained'
							onClick={handleSaveDescription}
							disabled={!editable || isSavingDescription}
						>
							Zapisz
						</Button>
					</DialogActions>
				</Dialog>
			)}
			{showOralDescription && (
				<Dialog
					open={previewDialogOpen}
					onClose={() => setPreviewDialogOpen(false)}
					fullWidth
					maxWidth='md'
				>
					<DialogTitle>Podgląd z formatowaniem</DialogTitle>
					<DialogContent>
						<Typography
							variant='body2'
							sx={{ mb: 2 }}
						>
							Podgląd pokazuje ręczne oraz automatyczne pogrubienia.
						</Typography>
						<div
							style={{ maxHeight: 500, overflow: 'auto' }}
							dangerouslySetInnerHTML={{
								__html: cleanUpHtml(applyAutoOralFormatting(descriptionValue), {
									ALLOWED_TAGS: ['b', 'strong', 'i', 'em', 'br', 'p', 'ul', 'ol', 'li'],
									ALLOWED_ATTR: [],
								}),
							}}
						/>
					</DialogContent>
					<DialogActions sx={{ justifyContent: 'space-between' }}>
						<Typography
							variant='caption'
							color='text.secondary'
						>
							Auto pogrubienie: linia ≤ 60 znaków, następna ≥ 125, poprzednia bez formatowania.
						</Typography>
						<Button onClick={() => setPreviewDialogOpen(false)}>Zamknij podgląd</Button>
					</DialogActions>
				</Dialog>
			)}
			{showOralDescription && (
				<Dialog
					open={importDialogOpen}
					onClose={() => setImportDialogOpen(false)}
					fullWidth
					maxWidth='md'
				>
					<DialogTitle>Import charakterystyk (Etap IIB)</DialogTitle>
					<DialogContent>
						<Typography
							variant='body2'
							sx={{ mb: 2 }}
						>
							Wklej treść protokołu z Worda. Weryfikujemy nagłówek tabeli oraz zgodność punktów z listą.
						</Typography>
						{districtFilterId === -1 && (
							<Typography
								variant='body2'
								color='error'
								sx={{ mb: 2 }}
							>
								Najpierw wybierz okręg z listy powyżej.
							</Typography>
						)}
						{importReport.length > 0 && (
							<Box sx={{ mt: 2 }}>
								<Typography variant='subtitle2'>Raport</Typography>
								{importReport.map((item, index) => (
									<Typography
										key={`${item.status}-${index}`}
										color={item.status === 'success' ? 'success.main' : 'error.main'}
										variant='body2'
									>
										{item.message}
									</Typography>
								))}
								<div ref={importReportEndRef} />
							</Box>
						)}
						<TextField
							fullWidth
							multiline
							minRows={10}
							value={importText}
							onChange={(event) => setImportText(event.target.value)}
							placeholder='Wklej protokół...'
							sx={{ mt: 2 }}
						/>
					</DialogContent>
					<DialogActions>
						<Button
							variant='outlined'
							onClick={handleClearImport}
						>
							Wyczyść
						</Button>
						<Button onClick={() => setImportDialogOpen(false)}>Zamknij</Button>
						<Button
							variant='contained'
							onClick={handleImport}
							disabled={importLoading}
						>
							{importLoading ? 'Importuję...' : 'Importuj'}
						</Button>
					</DialogActions>
				</Dialog>
			)}
			<ProtocolVerificationDialog
				open={verificationDialogOpen}
				onClose={() => setVerificationDialogOpen(false)}
				stageKey='IIB'
				title='Weryfikacja protokołu (Etap IIB)'
				students={displayedStudents}
			/>
			<ConfirmationDialog
				close={() => setConfirmDescriptionCloseText(null)}
				inProgress={false}
				text={confirmDescriptionCloseText}
				onSuccess={finishDescriptionDialogClose}
			/>
		</>
	);
};

export default SecondBStageTable;
